/**
 * CXone SDK Context
 *
 * Manages CXone SDK initialization and voice connection.
 */

import React, { createContext, useContext, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getCXoneClient, getVoiceClient } from '../services/cxone-sdk.service';
import { useCxoneSession } from './cxone/useCxoneSession';
import { useCxoneRecovery } from './cxone/useCxoneRecovery';
import { useCxoneLifecycle } from './cxone/useCxoneLifecycle';
import { CXoneContextValue } from './cxone/types';

const CXoneContext = createContext<CXoneContextValue | null>(null);

interface CXoneProviderProps {
  children: ReactNode;
}

export function CXoneProvider({ children }: CXoneProviderProps) {
  const { accessToken, user, isAuthenticated } = useAuth();
  const audioRef = useRef<HTMLAudioElement>(null);

  const {
    state,
    setState,
    voicePreference,
    hasSessionConflict,
    setHasSessionConflict,
    needsVoicePreference,
    setNeedsVoicePreference,
    refs,
    initializeWithVoicePreference,
    disconnect,
    clearSessionConflict,
    resetConnectionState,
  } = useCxoneSession({
    accessToken,
    cxoneAgentId: user?.cxoneAgentId,
    audioRef,
  });

  const {
    isSessionInvalid,
    isReconnecting,
    reconnect,
    handleSessionError,
    forceLogoutAndReconnect,
    resetRecoveryState,
  } = useCxoneRecovery({
    refs,
    initializeWithVoicePreference,
    setState,
    setHasSessionConflict,
    setNeedsVoicePreference,
  });

  useCxoneLifecycle({
    isAuthenticated,
    accessToken,
    user,
    needsVoicePreference,
    isInitialized: state.isInitialized,
    refs,
    initializeWithVoicePreference,
    setNeedsVoicePreference,
    resetConnectionState,
    resetRecoveryState,
    setState,
  });

  const value: CXoneContextValue = {
    ...state,
    audioRef,
    hasSessionConflict,
    needsVoicePreference,
    voicePreference,
    isSessionInvalid,
    isReconnecting,
    initializeWithVoicePreference,
    disconnect,
    forceLogoutAndReconnect,
    clearSessionConflict,
    handleSessionError,
    reconnect,
  };

  return (
    <CXoneContext.Provider value={value}>
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />
      {children}
    </CXoneContext.Provider>
  );
}

export function useCXone(): CXoneContextValue {
  const context = useContext(CXoneContext);
  if (!context) {
    throw new Error('useCXone must be used within a CXoneProvider');
  }
  return context;
}

export { getCXoneClient, getVoiceClient };

export default CXoneContext;
